// Portfolio content. Clients stay anonymous here. Only add a case when the owner has cleared it.
// publicCase: false keeps a case in the file for reference but out of the page.
// Cases marked false carry metrics the owner has not yet verified against code or documents.

export type CaseResult = { value: string; label: string }

export type CaseStudy = {
  id: string
  sector: string
  title: string
  summary: string
  stack: string[]
  results: CaseResult[]
  publicCase: boolean
}

export const cases: CaseStudy[] = [
  {
    id: 'telehealth-demo',
    sector: 'Healthcare demo build',
    title: 'Telehealth video consultation platform',
    summary:
      'A working demo of a video consultation platform for healthcare providers. It runs on fake data only and is covered by automated end-to-end tests.',
    stack: ['Next.js', 'PostgreSQL', 'Playwright'],
    results: [{ value: '15', label: 'automated test files' }],
    publicCase: true,
  },
  {
    id: 'synthetic-insurance-warehouse',
    sector: 'Insurance data',
    title: 'Synthetic insurance data warehouse',
    summary:
      'A generator that builds a realistic insurance data warehouse from synthetic data. Teams use it to test pipelines and demos without touching live customer records.',
    stack: ['Python'],
    results: [
      { value: '9', label: 'dimension tables' },
      { value: '6', label: 'fact tables' },
    ],
    publicCase: true,
  },
  {
    id: 'synthetic-marketing-warehouse',
    sector: 'Marketing data',
    title: 'Synthetic marketing data warehouse',
    summary:
      'The same generator approach applied to marketing data, so analytics work can be built and tested before real campaign data is available.',
    stack: ['Python'],
    results: [
      { value: '9', label: 'dimension tables' },
      { value: '4', label: 'fact tables' },
    ],
    publicCase: true,
  },

  // Held back until the owner confirms the figures. See the NDA survey report.
  {
    id: 'saas-multi-product',
    sector: 'B2B SaaS',
    title: 'Multi-product SaaS platform',
    summary:
      'One platform with eight integrated applications, central single sign-on and multi-tenant architecture.',
    stack: ['React', 'Node.js', 'PostgreSQL', 'NGINX', 'PM2'],
    results: [{ value: '99.5%', label: 'uptime target, not yet measured' }],
    publicCase: false,
  },
  {
    id: 'fx-trading',
    sector: 'Financial markets',
    title: 'Institutional FX trading system',
    summary:
      'A foreign exchange trading platform for high-volume institutional transactions.',
    stack: ['C# / .NET', 'Angular', 'Kafka', 'SQL Server', 'Kubernetes'],
    results: [{ value: '200K+', label: 'transactions a day (employer figure)' }],
    publicCase: false,
  },
  {
    id: 'retail-feasibility',
    sector: 'Property investment',
    title: 'Retail feasibility platform',
    summary:
      'An automated analysis platform for retail property development decisions.',
    stack: ['Ruby on Rails', 'React', 'Python', 'BigQuery', 'FastAPI'],
    results: [{ value: '12×', label: 'performance gain (CV figure, unverified)' }],
    publicCase: false,
  },
  {
    id: 'tax-compliance',
    sector: 'Tax and compliance',
    title: 'Tax compliance SaaS',
    summary:
      'A multi-tenant tax management platform with compliance modules and a configurable tax calculation engine.',
    stack: ['React', 'React Native', 'Node.js', 'PostgreSQL', 'Docker'],
    results: [{ value: '9', label: 'production modules (not found in code)' }],
    publicCase: false,
  },
  {
    id: 'consulting-operations',
    sector: 'Professional services',
    title: 'Consulting operations platform',
    summary:
      'One system for client relationships, a sales pipeline, staff allocation and financial operations.',
    stack: ['React', 'Tailwind', 'Express.js', 'PostgreSQL', 'JWT'],
    results: [{ value: '5', label: 'connected modules' }],
    publicCase: false,
  },
  {
    id: 'integration-hub',
    sector: 'Enterprise integration',
    title: 'Enterprise integration hub',
    summary:
      'A high-availability service bus connecting mobile services to back-end systems.',
    stack: ['Java EE', 'JBoss', 'ActiveMQ', 'Web Services'],
    results: [{ value: '99.99%', label: 'availability (CV figure, older work)' }],
    publicCase: false,
  },
]

export const publicCases = cases.filter((c) => c.publicCase)
